import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import httpRequest from '../utils/request';
import './support.css';
import { toast } from 'react-toastify';
import { useModalEscape } from '../utils/useModalEscape';

export default function SupportTicketForm({ tenantId: tenantIdProp, className, onCreated, buttonStyle = {} }) {
  const [open, setOpen] = useState(false);

  // Create Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);

  // Tabs & Inbox State
  const [tab, setTab] = useState('create'); // 'create' | 'inbox'
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'open', 'closed'

  const esRef = useRef(null);

  // ESC key handler for modal
  useModalEscape(open, () => { setOpen(false); setTab('create'); });

  // Prefer tenantId from redux store if available
  const tenantFromStore = useSelector((s) => s.tenant?.tenant?._id || s.tenant?.tenant?.id);
  const tenantId = tenantIdProp || tenantFromStore;
  const { user } = useSelector((s) => s.auth || {});

  function buildEventsUrl(token) {
    const base = (httpRequest.defaults && httpRequest.defaults.baseURL) || '/api';
    let originOrBase = base;
    if (!/^https?:\/\//i.test(base)) {
      originOrBase = window.location.origin + (base.startsWith('/') ? base : '/' + base);
    }
    return originOrBase.replace(/\/$/, '') + '/tenants/events?token=' + encodeURIComponent(token);
  }

  async function submit() {
    if (!tenantId) {
      toast.error('Tenant no disponible. Intenta recargar la página.');
      return;
    }
    setLoading(true);
    try {
      const body = { tenantId, title, description, priority };
      const res = await httpRequest.post('/tickets', body);
      const data = res.data;
      setOpen(false);
      setTitle('');
      setDescription('');
      setPriority('medium');
      if (onCreated) onCreated(data);
      toast.success('Ticket creado: ' + (data._id || 'ok'));
    } catch (e) {
      console.error('SupportTicketForm submit error', e);
      const msg = e?.response?.data?.message || e?.message || 'Error creando ticket';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const loadTickets = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoadingTickets(true);
      const res = await httpRequest.get(`/tenants/${tenantId}/tickets`);
      setTickets(res.data || []);
    } catch (e) {
      console.error('Error loading tickets', e);
    } finally { setLoadingTickets(false); }
  }, [tenantId]);

  const replyToTicket = async (ticketId, text, onDone) => {
    try {
      const res = await httpRequest.post(`/tickets/${ticketId}/comments`, { text });
      toast.success('Respuesta enviada');
      if (onDone) onDone(res.data);
      loadTickets(); // Refresh to see new comment and potential status change
    } catch (e) {
      console.error('Reply error', e);
      toast.error('No se pudo enviar la respuesta');
    }
  };

  const userToken = user?.token;

  useEffect(() => {
    if (!userToken) return;
    const url = buildEventsUrl(userToken);
    try {
      const es = new EventSource(url);
      esRef.current = es;
      es.addEventListener('ticket:comment', (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d && (!tenantId || String(d.tenantId) === String(tenantId))) {
            loadTickets();
          }
        } catch (_) { }
      });
      es.addEventListener('ticket:status', (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d && (!tenantId || String(d.tenantId) === String(tenantId))) {
            loadTickets();
          }
        } catch (_) { }
      });
      es.onerror = (e) => console.warn('SSE error (support modal)', e);
    } catch (e) {
      console.warn('EventSource not available', e);
    }
    return () => { try { if (esRef.current) esRef.current.close(); } catch (_) { } };
  }, [userToken, tenantId, loadTickets]);

  const supportDebug = (typeof window !== 'undefined') && new URLSearchParams(window.location.search).has('support_debug');
  const disabledOpen = !tenantId && !supportDebug;
  const [fullScreen, setFullScreen] = useState(false);

  // Filter logic
  const filteredTickets = useMemo(() => {
    if (filterStatus === 'all') return tickets;
    if (filterStatus === 'open') return tickets.filter(t => ['open', 'in_progress'].includes(t.status));
    if (filterStatus === 'closed') return tickets.filter(t => ['resolved', 'closed'].includes(t.status));
    return tickets;
  }, [tickets, filterStatus]);

  return (
    <div className={className || ''}>
      <button
        className={`support-btn`}
        style={buttonStyle}
        onClick={() => setOpen(true)}
        disabled={disabledOpen}
        title={disabledOpen ? 'Cargando datos del comercio...' : 'Solicitar soporte'}
      >
        Soporte
      </button>

      {open && (
        <div className="support-modal-overlay">
          <div className="support-modal-backdrop" onClick={() => { setOpen(false); setTab('create'); }} />
          {/* container */}
          <div className={fullScreen ? 'support-modal' : 'support-modal window'}>
            <div className="support-header">
              <div className="support-title">Soporte Técnico</div>
              <div className="support-actions">
                <button onClick={() => setFullScreen(f => !f)}>{fullScreen ? 'Modo ventana' : 'Pantalla completa'}</button>
                <button onClick={() => { setOpen(false); setTab('create'); }}>Cerrar</button>
              </div>
            </div>

            <div className="support-body">
              <div className="support-tabs">
                <div className={`support-tab ${tab === 'create' ? 'active' : ''}`} onClick={() => setTab('create')}>Crear Ticket</div>
                <div className={`support-tab ${tab === 'inbox' ? 'active' : ''}`} onClick={() => { setTab('inbox'); loadTickets(); }}>Mis Tickets</div>
              </div>

              {tab === 'create' && (
                <div className="animate-fade-in">
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div>
                      <label className="support-section-title">Asunto</label>
                      <input
                        className="support-input"
                        placeholder="Ej: Problema al facturar..."
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="support-section-title">Prioridad</label>
                      <select
                        className="support-select"
                        value={priority}
                        onChange={e => setPriority(e.target.value)}
                      >
                        <option value="low">Baja - Consulta general</option>
                        <option value="medium">Media - Incoveniente funcional</option>
                        <option value="high">Alta - Sistema bloqueado / Error crítico</option>
                      </select>
                    </div>

                    <div>
                      <label className="support-section-title">Descripción Detallada</label>
                      <textarea
                        className="support-input"
                        placeholder="Describe el problema con el mayor detalle posible..."
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        rows={6}
                      />
                    </div>
                  </div>
                  <div className="support-form actions" style={{ marginTop: 20 }}>
                    <button className="btn-secondary" onClick={() => setOpen(false)} disabled={loading}>Cancelar</button>
                    <button className="btn-primary" onClick={submit} disabled={loading || !title || !description}>
                      {loading ? 'Enviando...' : 'Enviar Ticket'}
                    </button>
                  </div>
                </div>
              )}

              {tab === 'inbox' && (
                <div className="animate-fade-in">
                  <div className="support-actions-row" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>Estado:</span>
                      <select
                        className="support-select small"
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                      >
                        <option value="all">Todos</option>
                        <option value="open">Abiertos / En Progreso</option>
                        <option value="closed">Cerrados / Resueltos</option>
                      </select>
                    </div>
                    <div>
                      <button className="btn-secondary small" onClick={loadTickets}>🔄 Actualizar</button>
                    </div>
                  </div>

                  <div className="tickets-container">
                    {loadingTickets ? (
                      <div className="loading-skeleton">
                        <div className="skeleton-line"></div>
                        <div className="skeleton-line"></div>
                        <div className="skeleton-line"></div>
                      </div>
                    ) : (
                      filteredTickets.length === 0 ? (
                        <div className="empty-state">
                          <div style={{ fontSize: 40 }}>📭</div>
                          <div style={{ fontSize: 14, color: '#6b7280', marginTop: 8 }}>No hay tickets en esta vista.</div>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 16 }}>
                          {filteredTickets.map(t => (
                            <TicketThread
                              key={t._id || t.id}
                              ticket={t}
                              onReply={(text, cb) => replyToTicket(t._id || t.id, text, cb)}
                            />
                          ))}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketThread({ ticket, onReply }) {
  const [openReply, setOpenReply] = useState(false);
  const [text, setText] = useState('');

  const statusLabels = {
    open: { text: 'Abierto', class: 'badge-open' },
    in_progress: { text: 'En Progreso', class: 'badge-progress' },
    resolved: { text: 'Resuelto', class: 'badge-resolved' },
    closed: { text: 'Cerrado', class: 'badge-closed' }
  };

  const priorityLabels = {
    low: { text: 'Baja', class: 'badge-low' },
    medium: { text: 'Media', class: 'badge-medium' },
    high: { text: 'Alta', class: 'badge-high' }
  };

  const st = statusLabels[ticket.status] || { text: ticket.status, class: 'badge-default' };
  const pr = priorityLabels[ticket.priority] || { text: ticket.priority || 'Normal', class: 'badge-default' };

  return (
    <div className="support-ticket">
      <div className="ticket-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{ticket.title}</div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>Ticket #{String(ticket._id || '').slice(-6).toUpperCase()}</div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span className={`ticket-badge ${st.class}`}>{st.text}</span>
          <span className={`ticket-badge ${pr.class}`}>{pr.text}</span>
        </div>
      </div>

      <div className="ticket-description">
        {ticket.description}
      </div>

      <div className="ticket-comments">
        {(ticket.comments || []).map((c, idx) => {
          // Identify if comment is from support (admin) or user
          // Logic: if authorId is 'ADMIN' or undefined/null/empty but authorName is 'Soporte', treat as support
          // Note, in backend: authorId: isAdmin ? 'ADMIN' : user._id
          const isSupport = c.authorId === 'ADMIN' || c.authorName === 'Soporte';

          return (
            <div key={idx} className={`chat-bubble-row ${isSupport ? 'left' : 'right'}`}>
              <div className={`chat-bubble ${isSupport ? 'support' : 'user'}`}>
                <div className="bubble-meta">
                  <span className="bubble-author">{c.authorName || 'Desconocido'}</span>
                  <span className="bubble-time">{new Date(c.createdAt).toLocaleString()}</span>
                </div>
                <div className="bubble-text">{c.text}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ticket-actions">
        {['resolved', 'closed'].includes(ticket.status) ? (
          <div className="ticket-closed-notice">Este ticket está cerrado. Responder lo reabrirá.</div>
        ) : null}

        {!openReply ? (
          <button className="btn-reply" onClick={() => setOpenReply(true)}>💬 Responder</button>
        ) : (
          <div className="reply-box animate-fade-in">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={3}
              className="support-input"
              placeholder="Escribe tu respuesta..."
              autoFocus
            />
            <div className="reply-actions">
              <button className="btn-secondary small" onClick={() => { setOpenReply(false); setText(''); }}>Cancelar</button>
              <button
                className="btn-primary small"
                disabled={!text.trim()}
                onClick={() => { if (text.trim()) { onReply(text, () => { setText(''); setOpenReply(false); }); } }}
              >
                Enviar Respuesta
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
